const eurFormatter = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  // In it-IT il separatore delle migliaia manca sotto 10.000 (1340,91 €)
  useGrouping: "always",
});

export const eur = (v) => eurFormatter.format(Number(v) || 0);

export const pct = (v) =>
  `${(Number(v) || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`;

export const kw = (v) =>
  `${(Number(v) || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kW`;

export const dateIt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("it-IT") : "";

export const addDaysIt = (iso, days) => {
  if (!iso) return "";
  const d = new Date(iso);
  d.setDate(d.getDate() + (Number(days) || 0));
  return d.toLocaleDateString("it-IT");
};

// Legge il messaggio d'errore {detail} dalle risposte dell'API PHP
export const apiError = async (res, fallback = "Errore imprevisto.") => {
  try {
    const json = await res.json();
    return json?.detail || fallback;
  } catch {
    return fallback;
  }
};

// Numero offerta leggibile: EN/26/0142
// Indirizzi delle pagine: ?p=quotes per una pagina, ?p=quotes.detail&qid=b4ccaa86 per un
// singolo elemento. L'id nell'indirizzo sono i primi 8 caratteri dell'id del record.
export const shortId = (id) => String(id ?? "").replace(/-/g, "").slice(0, 8);
export const findByShortId = (items, short) =>
  (short && items.find((x) => shortId(x.id) === short)) || null;
// "quotes.detail" -> ["quotes", "detail"]
export const pageParam = (params, fallback) => (params.get("p") || fallback).split(".");

// Stampa il preventivo: il titolo della pagina diventa il nome proposto per il PDF
// ("codice - nome cliente"), poi torna quello di prima.
export const printQuote = (q) => {
  const previous = document.title;
  const name = [q?.code, q?.customer?.name].filter(Boolean).join(" - ").replace(/[\\/:*?"<>|]+/g, " ").trim();
  if (name) document.title = name;
  window.addEventListener("afterprint", () => { document.title = previous; }, { once: true });
  window.print();
};

export const quoteNumber = (q) => {
  const yy = q?.created_at ? String(new Date(q.created_at).getFullYear()).slice(-2) : "--";
  return `EN/${yy}/${String(q?.number ?? "").padStart(4, "0")}`;
};

export const dateTimeIt = (iso) =>
  iso
    ? `${new Date(iso).toLocaleDateString("it-IT")}, ${new Date(iso).toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : "";

export const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

// Tipi di prodotto del listino (come QUOTE_PRODUCT_TYPES in api/quotes.php)
export const PRODUCT_TYPES = {
  modulo: "Moduli fotovoltaici",
  inverter_mono: "Inverter monofase",
  inverter_tri: "Inverter trifase",
  batteria: "Batterie",
  batteria_tri: "Batteria trifase",
  colonnina: "Colonnina di ricarica",
  backup: "Backup",
  extra: "Extra",
};

export const productTypeLabel = (t) => PRODUCT_TYPES[t] || t || "";

// Tipi che contano come accumulo (come QUOTE_BATTERY_TYPES in api/quotes.php)
export const BATTERY_TYPES = ["batteria", "batteria_tri"];
export const isBattery = (category) => BATTERY_TYPES.includes(category);

export const POWER_UNITS = ["W", "kW", "kWh", "CAD"];

// "505 W", "6 kW", "9,6 kWh". Le righe salvate prima delle unita' hanno solo
// power_kw (per le batterie era la capacita' in kWh).
export const powerLabel = (line) => {
  const value = line.power ?? line.power_kw;
  if (!value) return "";
  const unit = line.power_unit || (isBattery(line.category) ? "kWh" : "kW");
  return `${Number(value).toLocaleString("it-IT", { maximumFractionDigits: 3 })} ${unit}`;
};

// Modalita' di pagamento (come QUOTE_PAYMENT_MODES in api/quotes.php).
// `text` e' la dicitura del preventivo del cliente.
export const PAYMENT_MODES = {
  bonifico: {
    label: "Bonifico 50% + 50%",
    text: "Anticipo con bonifico bancario dopo il sopralluogo tecnico + Saldo con bonifico bancario a fine installazione",
  },
  finanziamento: {
    label: "Finanziamento",
    text: "NO ANTICIPO, 100% Finanziamento con prima rata a 90 giorni dall'installazione",
    // Con anticipo > 0
    textDeposit:
      "Anticipo con bonifico bancario dopo il sopralluogo tecnico + Finanziamento con prima rata a 90 giorni dall'installazione",
  },
  // Solo per i preventivi salvati prima dell'unificazione: non e' piu' selezionabile
  anticipo_finanziamento: {
    legacy: true,
    label: "Anticipo + finanziamento",
    text: "Anticipo con bonifico bancario dopo il sopralluogo tecnico + Finanziamento con prima rata a 90 giorni dall'installazione",
  },
};

export const COMMISSION_LEVEL_LABELS = { alta: "Alta", media: "Media", bassa: "Bassa", libera: "Libera" };

export const QUOTE_STATUSES = {
  bozza: { label: "Bozza", className: "bg-gray-200 text-gray-600" },
  valutazione: { label: "In valutazione", className: "bg-[#F4C542] text-[#0A1F44]" },
  inviato: { label: "Inviato", className: "bg-[#0A1F44] text-white" },
  accettato: { label: "Accettato", className: "bg-[#0FB36B] text-white" },
  rifiutato: { label: "Rifiutato", className: "bg-red-500 text-white" },
};

export const inputClass =
  "w-full px-3 h-10 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition";

export const labelClass =
  "block text-[11px] font-semibold text-[#0A1F44] mb-1 uppercase tracking-wider";
